/**
 * Marketplace × Industry Matrix — Feb 2026 (P2 SEO Sprint)
 *
 * Powers 52 "{Marketplace} {Industry} Consultant" pages (e.g.
 * /myntra-fashion-consultant, /nykaa-beauty-consultant,
 * /blinkit-fmcg-consultant). All render from
 * /pages/MarketplaceIndustryPage.jsx via this single config.
 *
 * Architecture:
 *   INDUSTRIES        – 10 industry attribute records (compliance, AOV,
 *                       common mistakes, success roadmap)
 *   RELEVANCE_MATRIX  – sparse map of marketplace × industry combos
 *                       that we build (skips irrelevant, e.g. Blinkit-Jewellery)
 *   PAGES             – flattened array of page configs (52 entries)
 *   PAGE_BY_SLUG      – lookup
 *   PAGE_PATHS        – auto-derived route list
 *   getPagesByMarketplace / getPagesByIndustry – helpers for cross-linking
 */
import { MARKETPLACE_ATTRS } from './marketplaceComparisonData';

// Extend attrs with Nykaa Fashion + Amazon Fashion as separate entries.
// Both share brand equity with the base marketplace but target the fashion vertical.
const INDUSTRY_MARKETPLACES = {
  ...MARKETPLACE_ATTRS,
  nykaaFashion: {
    ...MARKETPLACE_ATTRS.nykaa,
    name: 'Nykaa Fashion',
    slug: 'nykaa-fashion',
    logo: 'NF',
    accent: 'from-pink-500 to-rose-600',
    categoryFocus: 'Fashion, Footwear, Accessories, Jewellery, Kidswear',
    aovBand: '₹1,800-3,400',
    hubHref: '/nykaa-fashion-account-management',
    approvalHref: '/nykaa-fashion-seller-registration',
    brandSuit: 'Premium D2C fashion, footwear, accessories, jewellery — Nykaa\'s cross-sell audience',
  },
  amazonFashion: {
    ...MARKETPLACE_ATTRS.amazon,
    name: 'Amazon Fashion',
    slug: 'amazon-fashion',
    logo: 'AF',
    accent: 'from-slate-700 to-slate-900',
    categoryFocus: 'Fashion, Footwear, Jewellery, Kidswear, Home',
    aovBand: '₹550-1,400',
    hubHref: '/amazon-seller-account-management',
    approvalHref: '/amazon-brand-registry-india',
    brandSuit: 'Volume-first fashion brands, tier-2/3 reach, FBA-native operators',
  },
};

// ── 10 Industry attribute records ─────────────────────────────────────
export const INDUSTRIES = {
  fashion: {
    slug: 'fashion',
    name: 'Fashion',
    label: 'Fashion Brands',
    plural: 'fashion brands',
    icon: 'Shirt',
    accent: 'from-pink-500 to-rose-500',
    overview: 'India\'s fashion market is projected to reach $135B by 2028, with online penetration at 22% and growing 18% YoY. Marketplace-led fashion GMV crossed ₹75,000 Cr in FY25.',
    idealBrandProfile: '20+ SKUs, professional studio photography, GST + brand trademark (Class 25), ₹5L+ monthly ad-appetite, and a clear AOV target band.',
    complianceReqs: 'GST, brand trademark (Class 25), fibre-composition labeling, MRP + country-of-origin disclosure, and BIS marks where applicable.',
    catalogNeeds: 'Marketplace-spec studio shoots (5-7 angles per SKU), model-worn images, size chart PDFs, fabric care icons, and video content for hero SKUs.',
    typicalCommissions: '15-32%',
    growthDrivers: ['Editorial curation velocity', 'Sale event participation (EORS, Big Bold Sale)', 'PLA + Sponsored Brand ROAS >2.5x', 'Model-worn content refresh every 6-8 weeks', 'Size-inclusive catalog'],
    commonMistakes: [
      'Launching with <20 SKUs (buyers see low commitment)',
      'DIY / smartphone photography — instant rejection',
      'Ignoring return rate optimization (>25% RTO breaks unit economics)',
      'Under-investing in PLA in the first 90 days',
      'Missing seasonal collection windows (SS/AW filing 90 days pre-launch)',
    ],
    successRoadmap: [
      'Weeks 1-2: Agency filing, GST + trademark documentation, catalog audit',
      'Weeks 3-5: Studio shoot, size-chart preparation, PDP copy',
      'Weeks 6-8: Buyer meetings, approval, first-30-SKU catalog upload',
      'Weeks 9-12: PLA setup, first EORS/campaign participation, review-harvest kickoff',
      'Months 4-6: Sale event ROAS optimization, top-100-SKU expansion, content refresh',
    ],
    faqExtra: [
      { q: 'What are the fashion-specific compliance requirements?', a: 'GST, brand trademark (Class 25), fibre composition labels per Legal Metrology, MRP + country-of-origin disclosure, and BIS marks for specific sub-categories (helmets, footwear soles).' },
    ],
  },
  beauty: {
    slug: 'beauty',
    name: 'Beauty',
    label: 'Beauty Brands',
    plural: 'beauty brands',
    icon: 'Sparkles',
    accent: 'from-fuchsia-500 to-pink-500',
    overview: 'India\'s beauty market is projected to reach $28B by 2028, with online-first D2C brands driving 40% of new-brand launches. Marketplace beauty GMV crossed ₹18,500 Cr in FY25.',
    idealBrandProfile: 'D2C traction (Instagram/organic), FSSAI + CDSCO compliance stack, ingredient-story led brand book, ₹3L+ monthly ad-appetite.',
    complianceReqs: 'GST, FSSAI (nutraceuticals/wellness), CDSCO registration (cosmetics), BIS marks where applicable, ingredient disclosure, and shelf-life stickering.',
    catalogNeeds: 'Ingredient-story imagery, lifestyle/model shots, before-after where compliant, video reviews, texture close-ups, and swatch shots for makeup.',
    typicalCommissions: '20-35%',
    growthDrivers: ['Buyer-team pitch quality', 'Content velocity + editorial placement', 'Review harvesting (500+ within 90 days)', 'Sample-led acquisition', 'Sale event discount funding (Pink Pass etc.)'],
    commonMistakes: [
      'Launching without CDSCO for cosmetics — auto rejection',
      'Weak ingredient story — buyers ignore',
      'Skipping sample-led acquisition — organic reviews stall',
      'DIY photography — quality bar is highest in beauty',
      'Ignoring shade/skin-tone inclusive imagery',
    ],
    successRoadmap: [
      'Weeks 1-3: FSSAI + CDSCO documentation, brand book, buyer deck',
      'Weeks 4-6: Buyer meetings, ingredient story approval, catalog prep',
      'Weeks 7-9: Studio shoots, PDP copy, video content',
      'Weeks 10-12: Launch + sampler drops, review harvesting begins',
      'Months 4-6: Sale event participation, PLA scaling, top-50-SKU expansion',
    ],
    faqExtra: [
      { q: 'Do I need CDSCO to sell cosmetics on marketplaces?', a: 'Yes — CDSCO Cosmetic Import Registration (Form COS-1) is mandatory for all cosmetic products in India. Buyer teams verify CDSCO status before approval. This applies even for private-label / D2C brands.' },
    ],
  },
  cosmetics: {
    slug: 'cosmetics',
    name: 'Cosmetics',
    label: 'Cosmetics Brands',
    plural: 'cosmetics brands',
    icon: 'Palette',
    accent: 'from-rose-500 to-red-500',
    overview: 'India\'s cosmetics segment (colour + makeup) is the fastest-growing beauty vertical at 28% YoY. Marketplace cosmetics GMV crossed ₹8,200 Cr in FY25.',
    idealBrandProfile: 'CDSCO Cosmetic Import Registration, shade-inclusive catalog (12+ shades minimum for foundation/concealer), influencer-led social proof.',
    complianceReqs: 'GST, CDSCO Cosmetic Import Registration, ingredient disclosure, shelf-life labeling, and shade-code standardization.',
    catalogNeeds: 'Swatch shots on 5+ skin tones, texture close-ups, applied-on-model shots, tutorial videos, and pigmentation comparisons.',
    typicalCommissions: '22-35%',
    growthDrivers: ['Shade inclusivity (Fenty effect)', 'Influencer + micro-creator content', 'Tutorial video velocity', 'Bundling for AOV lift', 'Sale event participation'],
    commonMistakes: [
      'Launching with <8 shades — cannot win category ranking',
      'No tutorial video content — conversion caps at 3%',
      'Ignoring shade-code standardization (buyers get confused)',
      'Under-investing in influencer sampling',
    ],
    successRoadmap: [
      'Weeks 1-3: CDSCO, shade grid, brand book',
      'Weeks 4-6: Buyer meetings, catalog prep with swatches',
      'Weeks 7-9: Studio + tutorial videos, influencer sampling',
      'Weeks 10-12: Launch, PLA + Sponsored Brand campaigns',
      'Months 4-6: Sale events, top-shade expansion, bundling',
    ],
  },
  'personal-care': {
    slug: 'personal-care',
    name: 'Personal Care',
    label: 'Personal Care Brands',
    plural: 'personal care brands',
    icon: 'Droplet',
    accent: 'from-teal-500 to-cyan-500',
    overview: 'Personal care (haircare, bath & body, hygiene) is the largest beauty sub-vertical at ₹22,000 Cr online GMV in FY25. Quick Commerce is capturing 18%+ of new-brand sales.',
    idealBrandProfile: 'FSSAI + CDSCO stack, benefit-led brand narrative, repeat-purchase mechanics (subscription-friendly), and ₹150-450 unit AOV.',
    complianceReqs: 'GST, CDSCO (leave-on products), FSSAI (nutraceutical personal care), BIS marks where applicable.',
    catalogNeeds: 'Benefit-outcome imagery, ingredient close-ups, before-after (compliant claims), routine videos, and repeat-purchase reminders.',
    typicalCommissions: '20-30%',
    growthDrivers: ['Repeat-purchase optimization', 'Bundling routines', 'Quick Commerce dark-store velocity', 'Subscription conversion', 'Sample-led acquisition'],
    commonMistakes: [
      'Ignoring subscription conversion opportunity',
      'Weak repeat-purchase reminders — LTV caps',
      'Not adapting packaging for QC (<300g preferred)',
      'Under-investing in routine-based bundling',
    ],
    successRoadmap: [
      'Weeks 1-3: CDSCO + FSSAI, brand book',
      'Weeks 4-6: Buyer meetings, catalog prep, routine bundling',
      'Weeks 7-9: Studio, benefit-outcome content, packaging QC adaptation',
      'Weeks 10-12: Launch, subscription + repeat-purchase setup',
      'Months 4-6: Sample-led acquisition, top-SKU velocity optimization',
    ],
  },
  jewellery: {
    slug: 'jewellery',
    name: 'Jewellery',
    label: 'Jewellery Brands',
    plural: 'jewellery brands',
    icon: 'Gem',
    accent: 'from-amber-500 to-yellow-500',
    overview: 'India\'s jewellery market is $85B (BIS estimate 2025), with online penetration still <8% but growing 24% YoY. Marketplace jewellery GMV ≈ ₹6,500 Cr FY25.',
    idealBrandProfile: 'BIS Hallmark, GST, HUID compliance, insurance-covered logistics, and authenticity certification for silver/gold/lab-grown diamonds.',
    complianceReqs: 'GST, BIS Hallmark (mandatory for gold above 2g), HUID (Hallmark Unique ID), certificate of authenticity, insurance liability documentation.',
    catalogNeeds: 'High-res close-ups (5+ angles), 360° videos, lifestyle-on-model shots, purity/karat callouts, size-guide, and packaging shots.',
    typicalCommissions: '18-28%',
    growthDrivers: ['BIS Hallmark trust', 'Video-first PDPs', 'Gifting occasion campaigns', 'EMI/BNPL enablement', 'Return-friendly policy for confidence'],
    commonMistakes: [
      'Missing HUID on gold above 2g — buyer auto-reject',
      'Weak insurance/liability docs',
      'No 360° video — conversion <1.5%',
      'Ignoring gifting occasions (Rakhi, Diwali, Karva Chauth)',
    ],
    successRoadmap: [
      'Weeks 1-3: BIS Hallmark, HUID, insurance stack',
      'Weeks 4-6: Buyer meetings, catalog prep with certificates',
      'Weeks 7-9: 360° video shoots, model shots, packaging content',
      'Weeks 10-12: Launch, EMI/BNPL enablement',
      'Months 4-6: Gifting-occasion campaigns, top-SKU expansion',
    ],
  },
  footwear: {
    slug: 'footwear',
    name: 'Footwear',
    label: 'Footwear Brands',
    plural: 'footwear brands',
    icon: 'Footprints',
    accent: 'from-orange-500 to-amber-500',
    overview: 'India\'s footwear market is $18B, online growing 22% YoY. Marketplace footwear GMV ≈ ₹9,800 Cr FY25.',
    idealBrandProfile: 'BIS mark (sports + safety), size grid (UK 3-11 minimum), material-composition disclosure, and comfort-tech narrative.',
    complianceReqs: 'GST, brand trademark, BIS marks (sports/safety footwear), material disclosure per Legal Metrology.',
    catalogNeeds: 'Multi-angle shots (5+), on-foot lifestyle, size chart, material close-ups, sole-tech callouts.',
    typicalCommissions: '18-30%',
    growthDrivers: ['Size grid completeness (UK 3-11)', 'Comfort-tech narrative', 'Sale event participation', 'PLA + Sponsored Brand', 'Return-friendly policy'],
    commonMistakes: [
      'Launching with incomplete size grid',
      'DIY photography — low PDP conversion',
      'Ignoring return rate (RTO 25%+ if size chart weak)',
      'Weak sole-tech story for premium AOV',
    ],
    successRoadmap: [
      'Weeks 1-3: BIS + trademark, size grid audit',
      'Weeks 4-6: Buyer meetings, catalog prep, sole-tech content',
      'Weeks 7-9: Studio, on-foot shoots, size-chart PDFs',
      'Weeks 10-12: Launch, PLA setup, return-rate optimization',
      'Months 4-6: Sale events, top-SKU expansion',
    ],
  },
  'home-living': {
    slug: 'home-living',
    name: 'Home & Living',
    label: 'Home & Living Brands',
    plural: 'home & living brands',
    icon: 'Home',
    accent: 'from-emerald-500 to-teal-500',
    overview: 'India\'s home & living market is $50B, online at 12% penetration but growing 26% YoY. Marketplace home GMV ≈ ₹5,200 Cr FY25.',
    idealBrandProfile: 'BIS marks (electricals), lifestyle photography, dimension disclosure, care instructions, and packaging optimized for logistics.',
    complianceReqs: 'GST, BIS marks (electricals), textile fibre disclosure, care instructions, dimension + material labeling.',
    catalogNeeds: 'Lifestyle in-room shots, 360° videos, dimension diagrams, care icons, material close-ups.',
    typicalCommissions: '15-25%',
    growthDrivers: ['Lifestyle imagery', 'Bundling (2-seat + 3-seat, sheet-sets)', 'Sale event participation', 'Regional-language PDPs', 'Return-friendly policy'],
    commonMistakes: [
      'Incomplete dimension disclosure',
      'Weak care instructions',
      'Packaging-damage claims (high with home)',
      'DIY photography — home category needs styling',
    ],
    successRoadmap: [
      'Weeks 1-3: BIS + dimension audit, brand book',
      'Weeks 4-6: Buyer meetings, catalog prep, styling deck',
      'Weeks 7-9: In-room lifestyle shoots, videos, dimension diagrams',
      'Weeks 10-12: Launch, packaging QC + logistics setup',
      'Months 4-6: Bundling, top-SKU expansion, sale event participation',
    ],
  },
  kidswear: {
    slug: 'kidswear',
    name: 'Kidswear',
    label: 'Kidswear Brands',
    plural: 'kidswear brands',
    icon: 'Baby',
    accent: 'from-blue-500 to-indigo-500',
    overview: 'India\'s kidswear market is $22B, online growing 24% YoY. Marketplace kids GMV ≈ ₹4,800 Cr FY25.',
    idealBrandProfile: 'AZO-free certification, skin-safe fabric disclosure, age-grid completeness (0-14y), and safety documentation.',
    complianceReqs: 'GST, brand trademark, AZO-free certification, skin-safe fabric disclosure (BIS/OEKO-TEX), CPSIA where applicable.',
    catalogNeeds: 'Age-grid clear, model-worn shots, fabric close-ups, care instructions, safety callouts.',
    typicalCommissions: '18-28%',
    growthDrivers: ['AZO-free / OEKO-TEX certification', 'Age-grid completeness', 'Seasonal collections (SS/AW)', 'Gifting-occasion bundling', 'Return-friendly policy'],
    commonMistakes: [
      'Missing AZO-free / OEKO-TEX certification',
      'Weak age-grid (age 0-2 or 8-14 gaps)',
      'Ignoring gifting occasions (birthdays, festivals)',
      'DIY photography with unclear age indicators',
    ],
    successRoadmap: [
      'Weeks 1-3: AZO-free + safety documentation',
      'Weeks 4-6: Buyer meetings, age-grid audit, catalog prep',
      'Weeks 7-9: Model shoots (age-grid coverage), care content',
      'Weeks 10-12: Launch, gifting-bundle setup',
      'Months 4-6: Sale events, top-SKU expansion',
    ],
  },
  fmcg: {
    slug: 'fmcg',
    name: 'FMCG',
    label: 'FMCG Brands',
    plural: 'FMCG brands',
    icon: 'ShoppingCart',
    accent: 'from-yellow-500 to-orange-500',
    overview: 'India\'s FMCG market is $128B, Quick Commerce capturing 5% and growing 68% YoY. Marketplace FMCG GMV in QC ≈ ₹42,000 Cr FY25.',
    idealBrandProfile: 'FSSAI, GST, dark-store-friendly packaging (<300g preferred), high SKU velocity (>5 units/store/day), and margin discipline.',
    complianceReqs: 'GST, FSSAI, BIS marks, shelf-life labeling, MRP + country-of-origin, ingredient disclosure.',
    catalogNeeds: 'Packaging shots, ingredient close-ups, benefit-outcome imagery, usage videos, and shelf-life callouts.',
    typicalCommissions: '20-38%',
    growthDrivers: ['Dark-store SKU velocity', 'Bundling for AOV lift', 'Sale event participation', 'Sponsored Search + PLA', 'Repeat-purchase optimization'],
    commonMistakes: [
      'Packaging >300g — dark-store rejection',
      'Weak SKU velocity — auto-delisting',
      'Ignoring bundling for AOV',
      'Under-investing in Sponsored Search on QC',
    ],
    successRoadmap: [
      'Weeks 1-2: FSSAI, packaging QC audit',
      'Weeks 3-4: Buyer meetings, dark-store selection',
      'Weeks 5-6: Catalog prep, velocity commitments',
      'Weeks 7-8: Launch, Sponsored Search kick-off',
      'Months 3-6: Bundling, top-SKU velocity optimization',
    ],
  },
  'health-wellness': {
    slug: 'health-wellness',
    name: 'Health & Wellness',
    label: 'Health & Wellness Brands',
    plural: 'health & wellness brands',
    icon: 'Heart',
    accent: 'from-green-500 to-emerald-500',
    overview: 'India\'s health & wellness market is $32B, online growing 30% YoY. Marketplace + QC health/wellness GMV ≈ ₹8,500 Cr FY25.',
    idealBrandProfile: 'FSSAI (nutraceuticals), AYUSH license (if Ayurvedic), doctor/dietitian endorsement, and repeat-purchase mechanics.',
    complianceReqs: 'GST, FSSAI (nutraceuticals), AYUSH license (Ayurvedic), CDSCO (drugs/devices), doctor/dietitian endorsement documentation.',
    catalogNeeds: 'Benefit-outcome imagery, ingredient science, doctor/dietitian quotes, usage protocols, and repeat-purchase reminders.',
    typicalCommissions: '22-35%',
    growthDrivers: ['Doctor/dietitian endorsements', 'Ingredient-science content', 'Repeat-purchase optimization', 'Bundling protocols', 'Sample-led acquisition'],
    commonMistakes: [
      'Weak claims documentation — buyer rejection',
      'Missing doctor endorsement — low trust',
      'Ignoring subscription conversion',
      'Under-investing in ingredient-science content',
    ],
    successRoadmap: [
      'Weeks 1-3: FSSAI + AYUSH docs, doctor endorsements',
      'Weeks 4-6: Buyer meetings, ingredient science deck',
      'Weeks 7-9: Studio, protocol videos, repeat-purchase setup',
      'Weeks 10-12: Launch, sampler drops',
      'Months 4-6: Subscription conversion, top-SKU expansion',
    ],
  },
};

// ── Relevance matrix (only combos we build) ───────────────────────────
// Each entry can have optional overrides — most content is derived from
// INDUSTRIES + INDUSTRY_MARKETPLACES + a short combo-specific "whyFits" text.
// Structure: [marketplaceKey, industrySlug, whyFits]
const MATRIX_SPEC = [
  // Myntra × 8
  ['myntra', 'fashion',        'Myntra is India\'s #1 fashion marketplace with 70M+ MAU and ₹35,000 Cr fashion GMV. Fashion brands with 20+ SKUs and MAM-tier growth appetite should treat Myntra as their primary marketplace launch, not an add-on.'],
  ['myntra', 'beauty',         'Myntra Beauty is growing 40% YoY on the back of Myntra Studio content and its 70M+ MAU cross-sell audience. Beauty brands with fashion-adjacent positioning and content velocity get outsized discovery here.'],
  ['myntra', 'cosmetics',      'Cosmetics brands on Myntra benefit from Myntra Beauty\'s editorial curation, its 70M+ MAU fashion-native audience, and sale event mechanics (EORS Beauty) that drive velocity for shade-inclusive catalogs.'],
  ['myntra', 'personal-care',  'Personal care on Myntra is a fast-growing sub-vertical — Myntra Beauty extends into haircare, bath & body, and hygiene. Best fit for premium D2C brands with an ingredient-story narrative.'],
  ['myntra', 'jewellery',      'Myntra Jewellery (imitation + fine) targets the 70M+ MAU fashion audience with an AOV of ₹1,650-2,400. Best fit for occasion-driven demi-fine, imitation, and hallmarked silver.'],
  ['myntra', 'footwear',       'Footwear is Myntra\'s 3rd-largest category with dedicated Sponsored Brand + Video ad tools. Best fit for size-grid-complete (UK 3-11) fashion footwear with a comfort-tech story.'],
  ['myntra', 'home-living',    'Myntra Home is the newest vertical — small but Myntra is investing heavily in FY26. Early-mover fashion-home brands (bedding, decor, kitchen textiles) can lock in editorial placement now.'],
  ['myntra', 'kidswear',       'Myntra Kids serves the 70M+ MAU fashion parent audience. Best fit for AZO-free-certified, age-grid-complete kids brands with a seasonal collection cadence (SS/AW).'],

  // Nykaa Fashion × 7
  ['nykaaFashion', 'fashion',       'Nykaa Fashion serves Nykaa\'s 30M+ premium beauty MAU cross-sold into fashion. AOV runs 20-30% higher than Myntra. Best fit for premium D2C fashion brands with a beauty-adjacent aesthetic.'],
  ['nykaaFashion', 'beauty',        'Nykaa Fashion cross-sells with Nykaa Beauty seamlessly — dual-listing on both verticals amplifies discovery. Beauty brands with a fashion-forward positioning get outsized editorial support.'],
  ['nykaaFashion', 'cosmetics',     'Cosmetics on Nykaa Fashion targets shoppers already primed by Nykaa\'s beauty content. Shade-inclusive catalogs with tutorial-video content see 30-40% higher conversion than direct-catalog uploads.'],
  ['nykaaFashion', 'personal-care', 'Personal care on Nykaa Fashion works well for premium haircare, bath & body, and hygiene with fashion-forward branding. Repeat-purchase mechanics + subscription convert well here.'],
  ['nykaaFashion', 'jewellery',     'Nykaa Fashion Jewellery is a small but curated vertical — best fit for demi-fine, imitation, and gold-plated occasion pieces targeting Nykaa\'s premium female shopper.'],
  ['nykaaFashion', 'footwear',      'Nykaa Fashion Footwear focuses on premium AOV (₹1,800-3,400). Best fit for size-inclusive women\'s footwear, festive occasion styles, and comfort-tech premium brands.'],
  ['nykaaFashion', 'kidswear',      'Nykaa Fashion Kids serves premium D2C parents. Best fit for AZO-free-certified, age-grid-complete premium kidswear.'],

  // AJIO × 7
  ['ajio', 'fashion',       'AJIO\'s 3-track structure (Brand Mall / Luxe / Indie) fits every AOV band. Best fit for emerging D2C fashion, tier-2/3 mainstream, and AJIO Luxe designers. Reliance ecosystem cross-promo (JioMart, Trends) accelerates cold-start.'],
  ['ajio', 'footwear',      'Footwear on AJIO benefits from the 3-track structure — Brand Mall for mainstream (₹399-1,200), Luxe for premium (₹3,500+), and Indie for emerging D2C (₹1,200+). Reliance last-mile network is a plus.'],
  ['ajio', 'jewellery',     'AJIO Jewellery is a growing vertical with the 3-track flexibility. Best fit for imitation, demi-fine, and hallmarked silver — track determines your AOV lane.'],
  ['ajio', 'kidswear',      'AJIO Kids targets Reliance\'s tier-2/3 family audience. Best fit for AZO-free-certified, budget-forward kids brands with age-grid completeness.'],
  ['ajio', 'home-living',   'AJIO Home is a small but growing vertical — Reliance Trends store cross-promo + JioMart bundle-listing extend reach. Early-mover home brands can lock in placement.'],
  ['ajio', 'beauty',        'AJIO Beauty is a nascent vertical — Reliance is investing to compete with Nykaa/Myntra Beauty. Best fit for challenger beauty brands who want first-mover advantage.'],
  ['ajio', 'personal-care', 'Personal care on AJIO works for mainstream + premium D2C brands with a benefit-led narrative. Reliance Retail cross-promo (Trends store point-of-sale) is a growth lever.'],

  // Tata Cliq × 8
  ['tataCliq', 'fashion',       'Tata CLiQ Luxury + Palette serves premium and mainstream fashion respectively. Luxury (₹3,500+ AOV) has India\'s most curated designer lane; Palette (₹1,800-4,500) is mainstream cross-category.'],
  ['tataCliq', 'beauty',        'Tata CLiQ Luxury Beauty is India\'s premium beauty destination for niche luxury brands. Tata Neu loyalty + Croma cross-promo amplify reach.'],
  ['tataCliq', 'cosmetics',     'Cosmetics on Tata CLiQ Luxury targets HNW consumers with authenticity-verified premium brands. Shade-inclusive luxury cosmetics get outsized editorial placement.'],
  ['tataCliq', 'personal-care', 'Personal care on Tata CLiQ Palette works for premium D2C brands. Tata Neu loyalty + Croma / Trends cross-promo drive tier-1 metro reach.'],
  ['tataCliq', 'jewellery',     'Tata CLiQ Luxury Jewellery (with Tanishq + Zoya + CaratLane in ecosystem) is India\'s most trusted online jewellery destination. Best fit for BIS Hallmark + HUID compliant designer + fine jewellery.'],
  ['tataCliq', 'footwear',      'Footwear on Tata CLiQ Luxury targets premium designer footwear. Palette works for mainstream comfort-tech and formal footwear.'],
  ['tataCliq', 'home-living',   'Tata CLiQ Home benefits from cross-promo with Croma + Reliance Trends stores. Best fit for premium home decor, kitchen appliances, and heritage textile brands.'],
  ['tataCliq', 'kidswear',      'Tata CLiQ Kids targets Tata Neu\'s premium parent audience. Best fit for premium AZO-free-certified kidswear + gifting occasion collections.'],

  // Amazon Fashion × 5
  ['amazonFashion', 'fashion',     'Amazon Fashion is India\'s largest volume fashion channel by traffic (250M+ MAU) though editorially less curated than Myntra. Best fit for volume-first brands, tier-2/3 reach, and FBA-native operators.'],
  ['amazonFashion', 'footwear',    'Footwear on Amazon Fashion benefits from FBA + Prime + tier-2/3 reach. Best fit for volume-first brands with a complete size grid (UK 3-11).'],
  ['amazonFashion', 'jewellery',   'Amazon Jewellery works for BIS-Hallmark-compliant fine + imitation jewellery. Best fit for volume-first brands with EMI/BNPL enabled and insurance-covered logistics.'],
  ['amazonFashion', 'kidswear',    'Amazon Kids serves tier-2/3 volume-first parents. Best fit for AZO-free-certified brands with a broad age grid and repeat-purchase mechanics.'],
  ['amazonFashion', 'home-living', 'Amazon Home is India\'s largest online home marketplace. Best fit for volume-first home brands with FBA logistics + Prime Day sale participation.'],

  // Blinkit × 6
  ['blinkit', 'fmcg',            'Blinkit is India\'s #1 Quick Commerce platform (₹18,500 Cr GMV, Zomato-owned). FMCG brands with dark-store-friendly packaging (<300g), high SKU velocity, and Zomato cross-app appetite dominate here.'],
  ['blinkit', 'personal-care',   'Personal care on Blinkit targets impulse-buy shoppers — travel sizes, hero SKUs, and repeat-purchase mechanics work best. 15-min delivery drives velocity beyond traditional marketplaces.'],
  ['blinkit', 'beauty',          'Beauty on Blinkit is exploding — impulse-buy travel-size deodorants, hero SKUs, and daily-use essentials capture 15-min delivery demand. Best fit for D2C brands with mass appeal.'],
  ['blinkit', 'cosmetics',       'Cosmetics on Blinkit works for daily-use makeup essentials (kajal, liner, lip products). Best fit for hero-SKU-focused catalogs with dark-store-friendly packaging.'],
  ['blinkit', 'health-wellness', 'Health & wellness on Blinkit — protein bars, vitamins, immunity boosters, hydration drinks — capture impulse-buy demand. FSSAI + AYUSH compliance mandatory.'],
  ['blinkit', 'kidswear',        'Kidswear on Blinkit is limited to kids essentials — diapers, baby wipes, feeding accessories, safety products. Best fit for mass FMCG-style kids brands with high velocity.'],

  // Zepto × 6
  ['zepto', 'fmcg',            'Zepto (₹14,200 Cr GMV, 10-min delivery) is India\'s fastest-growing QC. FMCG brands targeting tier-1 metro premium shoppers should launch on Zepto for velocity + Zepto Ads self-serve.'],
  ['zepto', 'personal-care',   'Personal care on Zepto targets tier-1 metro premium shoppers with 10-min delivery. Premium D2C brands with hero SKUs + travel sizes see outsized velocity here.'],
  ['zepto', 'beauty',          'Beauty on Zepto — premium travel sizes, daily-essentials, and hero SKUs. Best fit for D2C beauty with tier-1 metro premium positioning.'],
  ['zepto', 'cosmetics',       'Cosmetics on Zepto focuses on daily-use hero SKUs (kajal, lip products, mascara). Zepto Ads self-serve is the best UI in QC for cosmetics velocity optimization.'],
  ['zepto', 'health-wellness', 'Health & wellness on Zepto — supplements, vitamins, immunity boosters, sports nutrition — capture tier-1 metro premium demand.'],
  ['zepto', 'kidswear',        'Kidswear on Zepto = kids essentials (diapers, wipes, baby food, safety products) for tier-1 metro premium parents. FMCG-style velocity mechanics.'],

  // Swiggy Instamart × 5
  ['instamart', 'fmcg',            'Swiggy Instamart (₹9,800 Cr GMV, inside Swiggy 500M+ MAU app) is India\'s F&B-strongest QC. FMCG + F&B brands leverage cross-app discovery from Swiggy food-delivery.'],
  ['instamart', 'personal-care',   'Personal care on Instamart benefits from Swiggy cross-app discovery. Best fit for hygiene, bath & body, hero SKUs with impulse-buy velocity.'],
  ['instamart', 'beauty',          'Beauty on Instamart — travel sizes, deodorants, daily essentials — reach 500M+ Swiggy cross-app MAU. Best fit for mass D2C beauty with velocity discipline.'],
  ['instamart', 'cosmetics',       'Cosmetics on Instamart focuses on daily-use hero SKUs with impulse-buy demand. Cross-app Swiggy discovery is a growth lever unique to Instamart.'],
  ['instamart', 'health-wellness', 'Health & wellness on Instamart — protein, vitamins, immunity, hydration — leverage Swiggy F&B cross-app discovery. FSSAI + AYUSH mandatory.'],
];

// ── Generate flat page config array ───────────────────────────────────
const build = (marketplaceKey, industrySlug, whyFits) => {
  const marketplace = INDUSTRY_MARKETPLACES[marketplaceKey];
  const industry = INDUSTRIES[industrySlug];
  // Dedupe repeated tokens: e.g. nykaa-fashion + fashion → nykaa-fashion-consultant (not -fashion-fashion-consultant)
  const slug = marketplace.slug.endsWith(`-${industry.slug}`) || marketplace.slug === industry.slug
    ? `${marketplace.slug}-consultant`
    : `${marketplace.slug}-${industry.slug}-consultant`;
  const displayIndustryToken = marketplace.name.toLowerCase().endsWith(` ${industry.name.toLowerCase()}`) ? '' : ` ${industry.name}`;
  return {
    slug,
    marketplace,
    industry,
    whyFits,
    metaTitle: `${marketplace.name}${displayIndustryToken} Consultant 2026 — Brand Approval & Growth | Zaroori Retail`,
    metaDescription: `Expert ${marketplace.name}${displayIndustryToken} consultant for ${industry.plural} in 2026. Brand approval in ${marketplace.timeline}, ${marketplace.commission} commission, category buyer relationships, PLA/PPC + catalog QC. Founder-led.`,
  };
};

export const PAGES = MATRIX_SPEC.map((spec) => build(spec[0], spec[1], spec[2]));

export const PAGE_BY_SLUG = Object.fromEntries(PAGES.map((p) => [p.slug, p]));

export const PAGE_PATHS = PAGES.map((p) => `/${p.slug}`);

// Cross-linking helpers ───────────────────────────────────────────────
export const getIndustryPagesByMarketplace = (marketplaceKey) => {
  const target =
    marketplaceKey === 'instamart' ? 'swiggy-instamart' :
    marketplaceKey === 'tataCliq' ? 'tata-cliq' :
    marketplaceKey === 'nykaaFashion' ? 'nykaa-fashion' :
    marketplaceKey === 'amazonFashion' ? 'amazon-fashion' :
    marketplaceKey;
  return PAGES.filter((p) => p.marketplace.slug === target);
};

export const getIndustryPagesByIndustry = (industrySlug) =>
  PAGES.filter((p) => p.industry.slug === industrySlug);

export const getRelatedIndustryPages = (currentSlug, limit = 6) =>
  PAGES.filter((p) => p.slug !== currentSlug).slice(0, limit);

export const INDUSTRY_LIST = Object.values(INDUSTRIES);
